import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { BackButtonContainer, BackButtonControl } from '~/components/granular/back-button';
import { DropdownMenu } from '~/components/granular/dropdown-menu';
import type { DropdownMenuProps } from '~/components/granular/dropdown-menu';
import { DropdownMenuButtonItem } from '~/components/granular/dropdown-menu/components';
import { Icon } from '~/components/granular/icon';
import type { IconType } from '~/components/icons';
import { useFormatMessage } from '~/intl';
import { classNames } from '~/utils';

/** The minimum an action needs to be listed; callers pass their own action objects straight in. */
export interface DrillDownMenuAction {
  id: string;
  label: string;
  icon?: IconType;
  disabled?: boolean;
}

export interface DrillDownMenuGroup<TAction extends DrillDownMenuAction = DrillDownMenuAction> {
  id: string;
  label: string;
  icon?: IconType;
  items: TAction[];
}

export type DrillDownMenuItem<TAction extends DrillDownMenuAction = DrillDownMenuAction> =
  | TAction
  | DrillDownMenuGroup<TAction>;

export interface DrillDownMenuProps<TAction extends DrillDownMenuAction> extends Omit<
  DropdownMenuProps,
  'children' | 'onEscapeKeyDown' | 'onSelect'
> {
  items: Array<DrillDownMenuItem<TAction>>;
  onSelect: (action: TAction) => void;
  backLabel?: string;
}

const isGroup = <TAction extends DrillDownMenuAction>(
  item: DrillDownMenuItem<TAction>,
): item is DrillDownMenuGroup<TAction> => 'items' in item;

/**
 * Where focus should land once the next level has rendered: its first entry, a specific entry by id, or
 * the menu itself (keeps arrow keys working without highlighting an entry the pointer isn't over).
 */
type PendingFocus = { kind: 'first' } | { kind: 'item'; id: string } | { kind: 'menu' };

/**
 * A dropdown menu whose group items replace the menu content with their children, with a back button
 * at the top — the same drill-in pattern as the dashboard combo filters. Radix has no primitive for
 * this (its submenus fly out), so the level swap is local state inside a regular Radix DropdownMenu.
 */
export function DrillDownMenu<TAction extends DrillDownMenuAction>({
  items,
  onSelect,
  backLabel,
  onOpenChange,
  contentClassname,
  ...props
}: DrillDownMenuProps<TAction>) {
  const formatMessage = useFormatMessage();
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const pendingFocus = useRef<PendingFocus | null>(null);
  const backSelectedWithPointer = useRef(false);

  const activeGroup = items.filter(isGroup).find((item) => item.id === activeGroupId);

  // Swapping levels unmounts the focused item, which would drop focus out of the menu and break
  // arrow-key navigation, so move it onto the new level explicitly.
  useEffect(() => {
    const target = pendingFocus.current;
    pendingFocus.current = null;
    if (!target || !listRef.current) {
      return;
    }
    if (target.kind === 'menu') {
      listRef.current.closest<HTMLElement>('[role="menu"]')?.focus();
      return;
    }
    // The back row carries no id, so 'first' skips it and lands on the first entry of the level.
    const entries = Array.from(listRef.current.querySelectorAll<HTMLElement>('[data-drill-down-id]'));
    const element = target.kind === 'first' ? entries[0] : entries.find((el) => el.dataset.drillDownId === target.id);
    element?.focus();
  }, [activeGroupId]);

  const openGroup = useCallback((groupId: string) => {
    pendingFocus.current = { kind: 'first' };
    setActiveGroupId(groupId);
  }, []);

  const goBack = useCallback(
    (focusTarget: 'group' | 'menu' = 'group') => {
      if (activeGroup) {
        pendingFocus.current = focusTarget === 'menu' ? { kind: 'menu' } : { kind: 'item', id: activeGroup.id };
      }
      setActiveGroupId(null);
    },
    [activeGroup],
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setActiveGroupId(null);
      }
      onOpenChange?.(open);
    },
    [onOpenChange],
  );

  const handleEscapeKeyDown = useCallback(
    (event: globalThis.KeyboardEvent) => {
      if (activeGroup) {
        event.preventDefault();
        goBack();
      }
    },
    [activeGroup, goBack],
  );

  const handleGroupLevelKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      backSelectedWithPointer.current = false;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goBack();
      }
    },
    [goBack],
  );

  const handleBackPointerDown = useCallback(() => {
    backSelectedWithPointer.current = true;
  }, []);

  // Radix highlights whichever entry has focus, so after a click the group row would stay highlighted
  // until the pointer moved. Only keyboard users get focus restored onto it.
  const handleBackSelect = useCallback(
    (event: Event) => {
      event.preventDefault();
      goBack(backSelectedWithPointer.current ? 'menu' : 'group');
    },
    [goBack],
  );

  return (
    <DropdownMenu
      {...props}
      contentClassname={classNames({ 'pt-0': !!activeGroup }, contentClassname)}
      onEscapeKeyDown={handleEscapeKeyDown}
      onOpenChange={handleOpenChange}
    >
      <div ref={listRef}>
        {activeGroup ? (
          <div aria-label={activeGroup.label} role="group">
            <BackButtonContainer>
              <RadixDropdownMenu.Item
                asChild
                onKeyDown={handleGroupLevelKeyDown}
                onPointerDown={handleBackPointerDown}
                onSelect={handleBackSelect}
              >
                <BackButtonControl className="focus-visible:outline-hidden data-highlighted:text-teal-700">
                  {backLabel ?? formatMessage({ id: 'common.back' })}
                </BackButtonControl>
              </RadixDropdownMenu.Item>
            </BackButtonContainer>
            <div className="pt-2">
              {activeGroup.items.map((action) => (
                <ActionItem action={action} key={action.id} onKeyDown={handleGroupLevelKeyDown} onSelect={onSelect} />
              ))}
            </div>
          </div>
        ) : (
          items.map((item) =>
            isGroup(item) ? (
              <GroupItem group={item} key={item.id} onOpen={openGroup} />
            ) : (
              <ActionItem action={item} key={item.id} onSelect={onSelect} />
            ),
          )
        )}
      </div>
    </DropdownMenu>
  );
}

/* -------------------------------------------------------------------------------------------------
 * Items
 * -----------------------------------------------------------------------------------------------*/

function ItemIcon({ icon }: { icon?: IconType }) {
  if (!icon) {
    return null;
  }
  return <Icon aria-hidden className="mr-2 shrink-0" height={16} icon={icon} width={16} />;
}

function ActionItem<TAction extends DrillDownMenuAction>({
  action,
  onKeyDown,
  onSelect,
}: {
  action: TAction;
  onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
  onSelect: (action: TAction) => void;
}) {
  const handleSelect = useCallback(() => onSelect(action), [action, onSelect]);

  return (
    <DropdownMenuButtonItem
      className="flex items-center"
      onKeyDown={onKeyDown}
      data-drill-down-id={action.id}
      disabled={action.disabled}
      onSelect={handleSelect}
    >
      <ItemIcon icon={action.icon} />
      {action.label}
    </DropdownMenuButtonItem>
  );
}

function GroupItem({ group, onOpen }: { group: DrillDownMenuGroup; onOpen: (groupId: string) => void }) {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        onOpen(group.id);
      }
    },
    [group.id, onOpen],
  );

  const handleSelect = useCallback(
    (event: Event) => {
      // Keep the menu open: selecting a group swaps the content instead of closing.
      event.preventDefault();
      onOpen(group.id);
    },
    [group.id, onOpen],
  );

  return (
    <DropdownMenuButtonItem
      aria-haspopup="menu"
      className="flex items-center"
      data-drill-down-id={group.id}
      onKeyDown={handleKeyDown}
      onSelect={handleSelect}
    >
      <ItemIcon icon={group.icon} />
      {group.label}
      <span className="ml-auto pl-4">
        <Icon aria-hidden height={16} icon="ArrowRightSIcon" width={16} />
      </span>
    </DropdownMenuButtonItem>
  );
}
